import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useParams } from "react-router-dom";
import axiosInstance from "../lib/axios";

const colors = {
  brand: "#2E3267",
  lightBg: "#F6F7FB",
  border: "#D0D4E4",
  heading: "#1A1C2B",
  text: "#333",
};

const emptyRow = {
  copies: "",
  name: "",
  type: "",
};

const defaultForm = {
  projectName: "",
  to: "",
  isClient: false,
  isConsultant: false,
  isContractor: false,

  rows: [{ ...emptyRow }],

  reasons: {
    check: false,
    sign: false,
    construction: false,
    submit: false,
    record: false,
  },

  sending: {
    office: false,
    courier: false,
    hand: false,
  },

  preparedBy: "",
  receiverName: "",
  receiverMob: "",
};

/* =========================================================
   CHECK BOX FOR PRINT
========================================================= */

const PrintCheck = ({ checked }) => (
  <span className={`print-check ${checked ? "checked" : ""}`}>
    {checked ? "✓" : ""}
  </span>
);

export default function Transmittal() {
  const { id } = useParams();

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [transNo, setTransNo] = useState("");
  const [form, setForm] = useState(defaultForm);

  const dateToday = new Date().toLocaleDateString("en-IN");

  /* =========================================================
     PROJECT NAME
  ========================================================= */

  const getProjectName = (p) =>
    p.name ||
    p.projectName ||
    p.project_name ||
    p.projectNo ||
    p.title ||
    p.projectTitle ||
    p.clientName ||
    p._id ||
    "";

  /* =========================================================
     NEW TRANSMITTAL NUMBER
  ========================================================= */

  useEffect(() => {
    if (id) return;

    async function fetchTransNo() {
      try {
        const res = await axiosInstance.get("/transmittal/last");
        const last = res.data;

        const todayKey =
          String(new Date().getDate()).padStart(2, "0") +
          String(new Date().getMonth() + 1).padStart(2, "0") +
          new Date().getFullYear();

        if (!last?.transNo) {
          setTransNo(`${todayKey}_1`);
        } else {
          const lastCount = parseInt(String(last.transNo).split("_")[1]) || 0;

          setTransNo(`${todayKey}_${lastCount + 1}`);
        }
      } catch (error) {
        console.log("Trans No Error:", error);

        const todayKey =
          String(new Date().getDate()).padStart(2, "0") +
          String(new Date().getMonth() + 1).padStart(2, "0") +
          new Date().getFullYear();

        setTransNo(`${todayKey}_1`);
      }
    }

    fetchTransNo();
  }, [id]);

  /* =========================================================
     EDIT DATA
  ========================================================= */

  useEffect(() => {
    if (!id) return;

    async function fetchEditData() {
      try {
        const res = await axiosInstance.get(`/transmittal/${id}`);
        const t = res.data || {};

        setForm({
          ...defaultForm,

          projectName: t.projectName || "",
          to: t.to || "",

          isClient: Boolean(t.isClient),
          isConsultant: Boolean(t.isConsultant),
          isContractor: Boolean(t.isContractor),

          rows:
            Array.isArray(t.rows) && t.rows.length ? t.rows : [{ ...emptyRow }],

          reasons: {
            ...defaultForm.reasons,
            ...(t.reasons || {}),
            sign: t.reasons?.sign || t.reasons?.signing || false,
          },

          sending: {
            ...defaultForm.sending,
            ...(t.sending || {}),
          },

          preparedBy: t.preparedBy || "",
          receiverName: t.receiverName || "",
          receiverMob: t.receiverMob || "",
        });

        setTransNo(t.transNo || "");
      } catch (err) {
        console.log("Edit load error:", err);
        alert("Transmittal load failed");
      }
    }

    fetchEditData();
  }, [id]);

  /* =========================================================
     PROJECTS
  ========================================================= */

  useEffect(() => {
    async function fetchProjects() {
      try {
        const res = await axiosInstance.get("/projects");
        const data = res.data;

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.projects)
            ? data.projects
            : Array.isArray(data?.data)
              ? data.data
              : Array.isArray(data?.result)
                ? data.result
                : [];

        setProjects(list);
      } catch (err) {
        console.log("Project fetch error:", err.response?.data || err.message);

        setProjects([]);
      }
    }

    fetchProjects();
  }, []);

  /* =========================================================
     USERS
  ========================================================= */

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await axiosInstance.get("/users?excludeSelf=0");

        const data = res.data;

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.users)
            ? data.users
            : Array.isArray(data?.data)
              ? data.data
              : [];

        setUsers(list);
      } catch (err) {
        console.log("Users fetch error:", err.response?.data || err.message);

        setUsers([]);
      }
    }

    fetchUsers();
  }, []);

  /* =========================================================
     ROW FUNCTIONS
  ========================================================= */

  const updateRow = (idx, field, value) => {
    const newRows = [...form.rows];

    newRows[idx] = {
      ...newRows[idx],
      [field]: value,
    };

    setForm({
      ...form,
      rows: newRows,
    });
  };

  const addRow = () => {
    setForm({
      ...form,
      rows: [
        ...form.rows,
        {
          ...emptyRow,
        },
      ],
    });
  };

  const deleteRow = (idx) => {
    const updated = form.rows.filter((_, i) => i !== idx);

    setForm({
      ...form,
      rows: updated.length ? updated : [{ ...emptyRow }],
    });
  };

  /* =========================================================
     SAVE
  ========================================================= */

  const saveTransmittal = async () => {
    if (!form.projectName) {
      return alert("Please select project");
    }

    if (!transNo) {
      return alert("Transmittal number missing");
    }

    const payload = {
      ...form,
      transNo,
      date: dateToday,
    };

    try {
      if (id) {
        await axiosInstance.put(`/transmittal/${id}`, payload);

        alert("Transmittal Updated Successfully!");

        window.location.href = "/transmittals";
        return;
      }

      await axiosInstance.post("/transmittal", payload);

      alert("Transmittal Saved Successfully!");

      setForm({
        ...defaultForm,
        rows: [{ ...emptyRow }],
      });

      const lastCount = parseInt(String(transNo).split("_")[1]) || 0;

      const todayKey =
        String(new Date().getDate()).padStart(2, "0") +
        String(new Date().getMonth() + 1).padStart(2, "0") +
        new Date().getFullYear();

      setTransNo(`${todayKey}_${lastCount + 1}`);
    } catch (error) {
      console.log(
        "Transmittal save error:",
        error.response?.data || error.message,
      );

      alert(error.response?.data?.message || "Server Error");
    }
  };

  /* =========================================================
     PROJECT OPTIONS
  ========================================================= */

  const projectOptions = projects.map((p) => {
    const name = getProjectName(p);

    return {
      value: name,
      label: name || "Unnamed Project",
    };
  });

  /* =========================================================
     PRINT

     IMPORTANT:
     Same page is now print-ready.
     Therefore:
     1. Print button works
     2. Ctrl + P works
     3. Portrait / Landscape can be changed in printer
  ========================================================= */

  const handlePrint = () => {
    if (document.activeElement) document.activeElement.blur();

    const printArea = document.getElementById("transmittalPrintArea");
    if (!printArea) {
      alert("Print content not found");
      return;
    }

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Transmittal Print");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Transmittal ${transNo || ""}</title>
<style>
@page { size: A4 portrait; margin: 0; }
* { box-sizing: border-box; }
html, body { width:210mm; height:297mm; margin:0; padding:0; background:#fff; color:#000; font-family:Arial, "Nirmala UI", sans-serif; }
body { overflow:hidden; }
.print-sheet { width:210mm; height:297mm; padding:7mm 8mm; display:flex; flex-direction:column; background:#fff; }
.print-copy { width:100%; flex:1 1 0; min-height:0; margin:0; padding:0; overflow:hidden; }
.copy-divider { flex:0 0 auto; border-top:1px dashed #777; margin:3mm 0; }
.p-header { display:flex; align-items:center; justify-content:space-between; gap:10px; height:13mm; }
.p-logo { width:27mm; max-height:11mm; object-fit:contain; }
.p-company { flex:1; text-align:right; }
.p-company-name { font-size:10.5pt; line-height:1.05; font-weight:700; }
.p-company-subtitle { margin-top:1px; font-size:5.7pt; line-height:1.05; }
.p-header-line { margin:1mm 0 1.4mm; border-top:1.4px solid #000; }
.p-title { margin:0 0 1.5mm; text-align:center; font-size:9.5pt; line-height:1.05; font-weight:700; }
.p-project-row { display:flex; justify-content:space-between; align-items:flex-end; gap:12mm; margin-bottom:1.3mm; }
.p-project { width:67%; } .p-number { width:24%; }
.p-label,.p-section-title { font-size:6.4pt; line-height:1.05; font-weight:700; }
.p-field,.p-prepared-field,.p-receiver-field,.p-to-field { margin-top:.5mm; padding:.7mm 1.2mm; border:1px solid #b9c0d0; border-radius:1px; font-size:6.5pt; line-height:1.05; }
.p-field { min-height:5mm; }
.p-gray { background:#f4f5f8; }
.p-to-row { display:flex; align-items:center; justify-content:space-between; gap:8px; margin:.8mm 0 1.3mm; }
.p-to-left { display:flex; align-items:center; gap:5px; flex:1; }
.p-to-label { font-size:6.8pt; } .p-to-field { width:34mm; min-height:5mm; }
.p-party { display:flex; align-items:center; gap:2px; white-space:nowrap; font-size:6.2pt; font-weight:600; }
.p-date { white-space:nowrap; font-size:6.2pt; }
.print-check { display:inline-flex; align-items:center; justify-content:center; width:3.2mm; height:3.2mm; flex:0 0 3.2mm; border:1px solid #222; font-size:6pt; font-weight:700; }
.p-table { width:100%; border-collapse:collapse; table-layout:fixed; font-size:6.2pt; }
.p-table th,.p-table td { height:5.2mm; padding:.65mm 1mm; border:1px solid #aeb7cb; vertical-align:middle; line-height:1.05; overflow-wrap:anywhere; }
.p-table th { background:#eef1f8; text-align:center; font-weight:700; }
.p-sr { width:7%; } .p-copies { width:10%; } .p-type { width:22%; } .p-center { text-align:center; }
.p-section { margin-top:1.2mm; }
.p-reason-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:1.5mm; margin-top:.8mm; }
.p-option { display:flex; align-items:flex-start; gap:2px; min-width:0; }
.p-option strong { display:block; font-size:5.9pt; line-height:1.02; }
.p-option span:not(.print-check) { display:block; margin-top:.2mm; font-size:5.2pt; line-height:1.02; }
.p-sending-row { display:flex; align-items:flex-start; gap:8mm; margin-top:.8mm; }
.p-prepared-row { display:grid; grid-template-columns:1fr 1fr; gap:22mm; margin-top:1.2mm; }
.p-prepared-field { width:78%; min-height:4.7mm; }
.p-signature-line { width:75%; margin-top:3.2mm; border-bottom:1px solid #222; }
.p-receiver { margin-top:1.2mm; }
.p-receiver-title { margin-bottom:.7mm; font-size:6.1pt; font-weight:700; }
.p-receiver-grid { display:grid; grid-template-columns:1fr 1fr; gap:22mm; }
.p-receiver-field { width:72%; min-height:4.4mm; font-size:5.8pt; }
.p-receiver-sign { margin-top:1.1mm; font-size:5.4pt; }
img { display:block; }
</style>
</head>
<body>${printArea.innerHTML}</body>
</html>`);
    doc.close();

    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => iframe.remove(), 1500);
      }, 300);
    };

    const imgs = Array.from(doc.images);
    if (!imgs.length || imgs.every((img) => img.complete)) doPrint();
    else {
      let done = 0;
      const finish = () => {
        done += 1;
        if (done >= imgs.length) doPrint();
      };
      imgs.forEach((img) => {
        img.onload = finish;
        img.onerror = finish;
      });
      setTimeout(doPrint, 1800);
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  /* =========================================================
     SCREEN FORM
  ========================================================= */

  const renderScreenCopy = (title) => (
    <div style={cardStyle}>
      {/* HEADER */}

      <div style={headerRow}>
        <img src="/Logo-sdc.jpg" style={{ width: 130 }} alt="Logo" />

        <div
          style={{
            textAlign: "right",
            fontSize: 13,
            color: colors.text,
          }}
        >
          <b style={{ fontSize: 15 }}>SHETH DESIGN CONSULTANTS LLP</b>
          <br />
          ARCHITECTURE | CIVIL CONSULTANCY | INTERIOR DESIGN | URBAN DESIGN
        </div>
      </div>

      <div
        style={{
          borderTop: "2px solid #000",
          marginBottom: 25,
          opacity: 0.8,
        }}
      />

      <h2 style={mainTitle}>TRANSMITTAL LETTER ({title})</h2>

      {/* PROJECT */}

      <div style={projectRow}>
        <div style={{ width: "60%" }}>
          <label
            style={{
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Project Name
          </label>

          <div style={{ width: "90%" }}>
            <Select
              options={projectOptions}
              value={
                form.projectName
                  ? {
                      value: form.projectName,
                      label: form.projectName,
                    }
                  : null
              }
              onChange={(s) =>
                setForm({
                  ...form,
                  projectName: s?.value || "",
                })
              }
              placeholder={
                projects.length ? "Select Project" : "No Project Found"
              }
              isClearable
            />
          </div>
        </div>

        <div style={{ width: "18%" }}>
          <label
            style={{
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Transmittal No.
          </label>

          <div style={infoBox}>{transNo}</div>
        </div>
      </div>

      {/* TO */}

      <div style={toRow}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 15,
          }}
        >
          <label
            style={{
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            To:
          </label>

          <input
            style={{
              ...inputBox,
              width: "250px",
              fontSize: 18,
              height: 38,
              padding: 8,
            }}
            value={form.to}
            onChange={(e) =>
              setForm({
                ...form,
                to: e.target.value,
              })
            }
          />

          {["Client", "Consultant", "Contractor"].map((label) => {
            const key = "is" + label;

            return (
              <label key={label} style={checkLabel}>
                <input
                  type="checkbox"
                  checked={Boolean(form[key])}
                  onChange={() =>
                    setForm({
                      ...form,
                      [key]: !form[key],
                    })
                  }
                  style={{
                    transform: "scale(1.7)",
                    cursor: "pointer",
                  }}
                />

                {label}
              </label>
            );
          })}
        </div>

        <div>
          <label
            style={{
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Date :
          </label>

          <span
            style={{
              marginLeft: 10,
              fontWeight: 600,
            }}
          >
            {dateToday}
          </span>
        </div>
      </div>

      {/* TABLE */}

      <table style={tableStyle}>
        <thead>
          <tr>
            <th
              style={{
                ...thStyle,
                width: 50,
              }}
            >
              Sr.
            </th>

            <th
              style={{
                ...thStyle,
                width: 60,
              }}
            >
              Copies
            </th>

            <th style={thStyle}>Document Name</th>

            <th
              style={{
                ...thStyle,
                width: 160,
              }}
            >
              Type
            </th>

            <th
              style={{
                ...thStyle,
                width: 60,
              }}
            />
          </tr>
        </thead>

        <tbody>
          {form.rows.map((r, idx) => (
            <tr key={idx}>
              <td style={tdStyle}>{idx + 1}</td>

              <td style={tdStyle}>
                <input
                  value={r.copies}
                  onChange={(e) => updateRow(idx, "copies", e.target.value)}
                  style={smallInput}
                />
              </td>

              <td style={tdStyle}>
                <input
                  value={r.name}
                  onChange={(e) => updateRow(idx, "name", e.target.value)}
                  style={fullInput}
                />
              </td>

              <td style={tdStyle}>
                <select
                  value={r.type}
                  onChange={(e) => updateRow(idx, "type", e.target.value)}
                  style={selectBox}
                >
                  <option value="">--</option>
                  <option value="Client Copy">Client Copy</option>
                  <option value="Contractor Copy">Contractor Copy</option>
                  <option value="Original Copy">Original Copy</option>
                  <option value="Duplicate Copy">Duplicate Copy</option>
                  <option value="Xerox Copy">Xerox Copy</option>
                  <option value="NA">NA</option>
                </select>
              </td>

              <td style={tdStyle}>
                <button
                  type="button"
                  onClick={() => deleteRow(idx)}
                  style={deleteBtn}
                >
                  X
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button type="button" onClick={addRow} style={addBtn}>
        + Add Row
      </button>

      {/* REASON */}

      <div style={{ marginTop: 25 }}>
        <div style={sectionHeading}>Reason For Sending :</div>

        <div style={reasonGrid}>
          {[
            ["check", "For Check", "(ચેક કરવા માટે)"],

            ["sign", "Signing & Returning", "(સહી કરી પરત કરવા)"],

            ["construction", "For Construction", "(બાંધકામ માટે)"],

            ["submit", "To Submit", "(સબમિટ કરવા માટે)"],

            ["record", "Just For Record", "(ફક્ત રેકોર્ડ માટે)"],
          ].map(([key, label, sub]) => (
            <label key={key} style={reasonLabelStyle}>
              <input
                type="checkbox"
                checked={Boolean(form.reasons[key])}
                onChange={() =>
                  setForm({
                    ...form,

                    reasons: {
                      ...form.reasons,
                      [key]: !form.reasons[key],
                    },
                  })
                }
                style={checkboxStyle}
              />

              {label}

              <span style={reasonSubText}>{sub}</span>
            </label>
          ))}
        </div>
      </div>

      {/* SENDING */}

      <div style={{ marginTop: 25 }}>
        <div style={sectionHeading}>Sending Via :</div>

        {[
          ["office", "From Office", "(ઓફિસ થી)"],

          ["courier", "Via Courier/Post", "(કુરિયર / પોસ્ટ દ્વારા)"],

          ["hand", "Hand-delivery", "(હાથેધરાઈ દ્વારા)"],
        ].map(([key, label, sub]) => (
          <label key={key} style={sendingLabel}>
            <input
              type="checkbox"
              checked={Boolean(form.sending[key])}
              onChange={() =>
                setForm({
                  ...form,

                  sending: {
                    ...form.sending,
                    [key]: !form.sending[key],
                  },
                })
              }
              style={{
                transform: "scale(1.6)",
                accentColor: colors.brand,
              }}
            />

            <span>
              {label}

              <br />

              <span
                style={{
                  fontSize: 15,
                  fontWeight: 400,
                }}
              >
                {sub}
              </span>
            </span>
          </label>
        ))}
      </div>

      {/* PREPARED */}

      <div style={preparedGrid}>
        <div>
          <div style={sectionHeading}>Prepared By</div>

          <select
            style={{
              ...inputBox,
              width: 400,
              height: 45,
            }}
            value={form.preparedBy}
            onChange={(e) =>
              setForm({
                ...form,
                preparedBy: e.target.value,
              })
            }
          >
            <option value="">-- Select User --</option>

            {users.map((u) => (
              <option key={u._id || u.id || u.name} value={u.name}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div
            style={{
              ...sectionHeading,
              marginTop: 5,
            }}
          >
            Signature
          </div>

          <div style={{ marginTop: 25 }}>
            ______________________________________
          </div>
        </div>
      </div>

      {/* RECEIVER */}

      {title === "Office Copy" && (
        <div style={{ marginTop: 35 }}>
          <h4 style={{ marginBottom: 15 }}>
            Receiver&apos;s Details (લઈજનારની વિગત) :
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 60,
            }}
          >
            <div>
              <div style={sectionHeading}>Receiver Name</div>

              <input
                style={{
                  ...inputBox,
                  width: "50%",
                  height: 38,
                }}
                value={form.receiverName}
                onChange={(e) =>
                  setForm({
                    ...form,
                    receiverName: e.target.value,
                  })
                }
              />

              <div style={{ marginTop: 25 }}>
                <b>Signature:</b> _________________________________
              </div>
            </div>

            <div>
              <div style={sectionHeading}>Mobile No</div>

              <input
                style={{
                  ...inputBox,
                  width: "50%",
                  height: 38,
                }}
                value={form.receiverMob}
                onChange={(e) =>
                  setForm({
                    ...form,
                    receiverMob: e.target.value,
                  })
                }
              />

              <div style={{ marginTop: 25 }}>
                <b>Date:</b> _________________________________
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* =========================================================
     PRINT COPY

     IMPORTANT:
     This is rendered in DOM all the time,
     but hidden on screen.

     Because it exists in the DOM, Ctrl+P can print it.
  ========================================================= */

  const renderPrintCopy = (title, includeReceiver = false) => (
    <section className="print-copy">
      {/* HEADER */}

      <div className="p-header">
        <img src="/Logo-sdc.jpg" className="p-logo" alt="SDC Logo" />

        <div className="p-company">
          <div className="p-company-name">SHETH DESIGN CONSULTANTS LLP</div>

          <div className="p-company-subtitle">
            ARCHITECTURE | CIVIL CONSULTANCY | INTERIOR DESIGN | URBAN DESIGN
          </div>
        </div>
      </div>

      <div className="p-header-line" />

      <div className="p-title">TRANSMITTAL LETTER ({title})</div>

      {/* PROJECT */}

      <div className="p-project-row">
        <div className="p-project">
          <div className="p-label">Project Name</div>

          <div className="p-field">{form.projectName || "\u00A0"}</div>
        </div>

        <div className="p-number">
          <div className="p-label">Transmittal No.</div>

          <div className="p-field p-gray">{transNo || "\u00A0"}</div>
        </div>
      </div>

      {/* TO */}

      <div className="p-to-row">
        <div className="p-to-left">
          <strong className="p-to-label">To:</strong>

          <div className="p-to-field">{form.to || "\u00A0"}</div>

          <div className="p-party">
            <PrintCheck checked={form.isClient} />
            Client
          </div>

          <div className="p-party">
            <PrintCheck checked={form.isConsultant} />
            Consultant
          </div>

          <div className="p-party">
            <PrintCheck checked={form.isContractor} />
            Contractor
          </div>
        </div>

        <div className="p-date">
          <strong>Date :</strong> {dateToday}
        </div>
      </div>

      {/* TABLE */}

      <table className="p-table">
        <thead>
          <tr>
            <th className="p-sr">Sr.</th>

            <th className="p-copies">Copies</th>

            <th>Document Name</th>

            <th className="p-type">Type</th>
          </tr>
        </thead>

        <tbody>
          {form.rows.map((row, index) => (
            <tr key={index}>
              <td className="p-center">{index + 1}</td>

              <td className="p-center">{row.copies}</td>

              <td>{row.name}</td>

              <td className="p-center">{row.type}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* REASON */}

      <div className="p-section">
        <div className="p-section-title">Reason For Sending :</div>

        <div className="p-reason-grid">
          <div className="p-option">
            <PrintCheck checked={form.reasons.check} />

            <div>
              <strong>For Check</strong>
              <span>(ચેક કરવા માટે)</span>
            </div>
          </div>

          <div className="p-option">
            <PrintCheck checked={form.reasons.sign} />

            <div>
              <strong>Signing & Returning</strong>
              <span>(સહી કરી પરત કરવા)</span>
            </div>
          </div>

          <div className="p-option">
            <PrintCheck checked={form.reasons.construction} />

            <div>
              <strong>For Construction</strong>
              <span>(બાંધકામ માટે)</span>
            </div>
          </div>

          <div className="p-option">
            <PrintCheck checked={form.reasons.submit} />

            <div>
              <strong>To Submit</strong>
              <span>(સબમિટ કરવા માટે)</span>
            </div>
          </div>

          <div className="p-option">
            <PrintCheck checked={form.reasons.record} />

            <div>
              <strong>Just For Record</strong>
              <span>(ફક્ત રેકોર્ડ માટે)</span>
            </div>
          </div>
        </div>
      </div>

      {/* SENDING */}

      <div className="p-section">
        <div className="p-section-title">Sending Via :</div>

        <div className="p-sending-row">
          <div className="p-option">
            <PrintCheck checked={form.sending.office} />

            <div>
              <strong>From Office</strong>
              <span>(ઓફિસ થી)</span>
            </div>
          </div>

          <div className="p-option">
            <PrintCheck checked={form.sending.courier} />

            <div>
              <strong>Via Courier/Post</strong>
              <span>(કુરિયર / પોસ્ટ દ્વારા)</span>
            </div>
          </div>

          <div className="p-option">
            <PrintCheck checked={form.sending.hand} />

            <div>
              <strong>Hand-delivery</strong>
              <span>(હાથેધરાઈ દ્વારા)</span>
            </div>
          </div>
        </div>
      </div>

      {/* PREPARED */}

      <div className="p-prepared-row">
        <div>
          <div className="p-section-title">Prepared By</div>

          <div className="p-prepared-field">{form.preparedBy || "\u00A0"}</div>
        </div>

        <div>
          <div className="p-section-title">Signature</div>

          <div className="p-signature-line" />
        </div>
      </div>

      {/* RECEIVER */}

      {includeReceiver && (
        <div className="p-receiver">
          <div className="p-receiver-title">
            Receiver&apos;s Details (લઈજનારની વિગત) :
          </div>

          <div className="p-receiver-grid">
            <div>
              <div className="p-section-title">Receiver Name</div>

              <div className="p-receiver-field">
                {form.receiverName || "\u00A0"}
              </div>

              <div className="p-receiver-sign">
                <strong>Signature:</strong> ___________________________
              </div>
            </div>

            <div>
              <div className="p-section-title">Mobile No</div>

              <div className="p-receiver-field">
                {form.receiverMob || "\u00A0"}
              </div>

              <div className="p-receiver-sign">
                <strong>Date:</strong> ___________________________
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );

  /* =========================================================
     RETURN
  ========================================================= */

  return (
    <div
      className="transmittal-main"
      style={{
        padding: 30,
        background: colors.lightBg,
      }}
    >
      {/* =========================
          NORMAL SCREEN
      ========================== */}

      <div className="screen-content">
        {renderScreenCopy("Office Copy")}

        {renderScreenCopy("Receiver Copy")}
      </div>

      {/* =========================
          PRINT CONTENT

          Hidden normally.
          Visible automatically for Ctrl+P.
      ========================== */}

      <div id="transmittalPrintArea" className="print-area">
        <div className="print-sheet">
          {renderPrintCopy("Office Copy", true)}

          <div className="copy-divider" />

          {renderPrintCopy("Receiver Copy", false)}
        </div>
      </div>

      {/* =========================
          BUTTONS
      ========================== */}

      <div className="footer-buttons" style={footerBar}>
        <button type="button" style={footerBtn} onClick={handlePrint}>
          🖨 Print
        </button>

        <button type="button" style={footerBtn} onClick={saveTransmittal}>
          💾 Save
        </button>

        <button
          type="button"
          style={{
            ...footerBtn,
            background: "#00897B",
          }}
          onClick={() => (window.location.href = "/transmittals")}
        >
          📄 View All
        </button>
      </div>

      {/* =====================================================
          PRINT CSS
      ===================================================== */}

      <style>
        {`

          /* ===============================================
             NORMAL SCREEN
          =============================================== */

          .print-area {
            display: none;
          }


          /* ===============================================
             PRINT

             IMPORTANT:
             No "size: A4 portrait".
             This means browser/printer controls orientation.

             Portrait selected = Portrait.
             Landscape selected = Landscape.
          =============================================== */

          @page {
            margin: 4mm;
          }

          @media print {

            html,
            body,
            #root {
              margin: 0 !important;
              padding: 0 !important;

              width: 100% !important;
              height: auto !important;

              min-height: 0 !important;

              overflow: visible !important;

              background: #ffffff !important;
            }


            /*
             * Hide normal ERP page.
             *
             * IMPORTANT:
             * We use display:none,
             * NOT visibility:hidden.
             */

            .screen-content,
            .footer-buttons {
              display: none !important;
            }


            .transmittal-main {
              display: block !important;

              width: 100% !important;

              margin: 0 !important;
              padding: 0 !important;

              background: #ffffff !important;

              overflow: visible !important;
            }


            /*
             * Print content exists in DOM.
             *
             * Therefore Ctrl + P works.
             */

            .print-area {
              display: block !important;

              width: 100% !important;

              margin: 0 !important;
              padding: 0 !important;

              background: #ffffff !important;

              color: #000000 !important;

              font-family:
                Arial,
                "Nirmala UI",
                "Noto Sans Gujarati",
                sans-serif !important;

              overflow: visible !important;
            }


            /*
             * ONE PHYSICAL SHEET
             *
             * Office + Receiver both inside this.
             */

            .print-sheet {
              display: block !important;

              width: 100% !important;

              margin: 0 !important;
              padding: 0 !important;

              background: #ffffff !important;

              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }


            /*
             * Each form takes roughly half sheet.
             */

            .print-copy {
              display: block !important;

              width: 100% !important;

              margin: 0 !important;

              padding: 1.5mm 3mm !important;

              box-sizing: border-box !important;

              background: #ffffff !important;

              color: #000000 !important;

              page-break-before: auto !important;
              page-break-after: auto !important;

              break-before: auto !important;
              break-after: auto !important;

              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }


            /*
             * Divider between Office / Receiver.
             */

            .copy-divider {
              width: 100% !important;

              height: 0 !important;

              margin: 1.5mm 0 !important;

              border-top:
                1px dashed #888 !important;
            }


            /* ===============================================
               HEADER
            =============================================== */

            .p-header {
              display: flex !important;

              align-items: center !important;

              justify-content:
                space-between !important;

              gap: 10px !important;
            }


            .p-logo {
              display: block !important;

              width: 75px !important;

              max-height: 38px !important;

              object-fit: contain !important;
            }


            .p-company {
              flex: 1 !important;

              text-align: right !important;
            }


            .p-company-name {
              font-size: 10px !important;

              line-height: 1.1 !important;

              font-weight: 700 !important;
            }


            .p-company-subtitle {
              margin-top: 1px !important;

              font-size: 6.5px !important;

              line-height: 1.1 !important;
            }


            .p-header-line {
              margin-top: 2mm !important;
              margin-bottom: 1.5mm !important;

              border-top:
                1.5px solid #000 !important;
            }


            .p-title {
              margin:
                0 0 1.5mm 0 !important;

              text-align: center !important;

              font-size: 10px !important;

              line-height: 1.1 !important;

              font-weight: 700 !important;

              letter-spacing:
                0.2px !important;
            }


            /* ===============================================
               PROJECT
            =============================================== */

            .p-project-row {
              display: flex !important;

              justify-content:
                space-between !important;

              align-items:
                flex-end !important;

              gap: 15px !important;

              margin-bottom:
                1.5mm !important;
            }


            .p-project {
              width: 67% !important;
            }


            .p-number {
              width: 24% !important;
            }


            .p-label,
            .p-section-title {
              font-size: 7.5px !important;

              line-height: 1.1 !important;

              font-weight: 700 !important;
            }


            .p-field {
              display: flex !important;

              align-items:
                center !important;

              min-height:
                5.5mm !important;

              margin-top:
                0.7mm !important;

              padding:
                1mm 1.5mm !important;

              border:
                1px solid #C9CEDA !important;

              border-radius:
                2px !important;

              font-size: 7.5px !important;

              line-height: 1.1 !important;
            }


            .p-gray {
              background:
                #F0F1F6 !important;
            }


            /* ===============================================
               TO + DATE
            =============================================== */

            .p-to-row {
              display: flex !important;

              align-items:
                center !important;

              justify-content:
                space-between !important;

              gap: 10px !important;

              margin:
                1mm 0 1.5mm !important;
            }


            .p-to-left {
              display: flex !important;

              align-items:
                center !important;

              gap: 6px !important;

              flex: 1 !important;
            }


            .p-to-label {
              font-size: 8px !important;
            }


            .p-to-field {
              width: 130px !important;

              min-height:
                5mm !important;

              padding:
                1mm 1.5mm !important;

              border:
                1px solid #C9CEDA !important;

              border-radius:
                2px !important;

              font-size: 7.5px !important;
            }


            .p-party {
              display: flex !important;

              align-items:
                center !important;

              gap: 3px !important;

              white-space:
                nowrap !important;

              font-size: 7px !important;

              font-weight:
                600 !important;
            }


            .p-date {
              white-space:
                nowrap !important;

              font-size: 7px !important;
            }


            /* ===============================================
               PRINT CHECKBOX
            =============================================== */

            .print-check {
              display:
                inline-flex !important;

              align-items:
                center !important;

              justify-content:
                center !important;

              width: 3.2mm !important;

              height: 3.2mm !important;

              flex:
                0 0 3.2mm !important;

              border:
                1px solid #222 !important;

              color: #000 !important;

              background:
                #fff !important;

              font-size: 7px !important;

              line-height: 1 !important;

              font-weight:
                700 !important;

              box-sizing:
                border-box !important;
            }


            /* ===============================================
               TABLE
            =============================================== */

            .p-table {
              width: 100% !important;

              margin: 0 !important;

              border-collapse:
                collapse !important;

              table-layout:
                fixed !important;

              font-size: 7px !important;
            }


            .p-table th,
            .p-table td {
              height:
                5.5mm !important;

              padding:
                0.8mm 1.2mm !important;

              border:
                1px solid #BFC4D3 !important;

              vertical-align:
                middle !important;

              line-height: 1.1 !important;

              overflow-wrap:
                anywhere !important;
            }


            .p-table th {
              background:
                #E4E8FF !important;

              text-align:
                center !important;

              font-size:
                7px !important;

              font-weight:
                700 !important;
            }


            .p-sr {
              width: 7% !important;
            }


            .p-copies {
              width: 10% !important;
            }


            .p-type {
              width: 22% !important;
            }


            .p-center {
              text-align:
                center !important;
            }


            /* ===============================================
               REASON / SENDING
            =============================================== */

            .p-section {
              margin-top:
                1.5mm !important;
            }


            .p-reason-grid {
              display: grid !important;

              grid-template-columns:
                repeat(5, 1fr) !important;

              gap:
                2mm !important;

              margin-top:
                1mm !important;
            }


            .p-option {
              display: flex !important;

              align-items:
                flex-start !important;

              gap: 3px !important;

              min-width: 0 !important;
            }


            .p-option strong {
              display: block !important;

              font-size: 6.5px !important;

              line-height: 1.05 !important;
            }


            .p-option span:not(.print-check) {
              display: block !important;

              margin-top:
                0.4mm !important;

              font-size: 5.8px !important;

              line-height: 1.05 !important;
            }


            .p-sending-row {
              display: flex !important;

              align-items:
                flex-start !important;

              gap: 25px !important;

              margin-top:
                1mm !important;
            }


            /* ===============================================
               PREPARED
            =============================================== */

            .p-prepared-row {
              display: grid !important;

              grid-template-columns:
                1fr 1fr !important;

              gap: 30px !important;

              margin-top:
                1.5mm !important;
            }


            .p-prepared-field {
              display: flex !important;

              align-items:
                center !important;

              width: 80% !important;

              min-height:
                5mm !important;

              margin-top:
                0.7mm !important;

              padding:
                1mm 1.5mm !important;

              border:
                1px solid #C9CEDA !important;

              border-radius:
                2px !important;

              font-size: 7px !important;
            }


            .p-signature-line {
              width: 75% !important;

              margin-top:
                4mm !important;

              border-bottom:
                1px solid #222 !important;
            }


            /* ===============================================
               RECEIVER
            =============================================== */

            .p-receiver {
              margin-top:
                1.5mm !important;
            }


            .p-receiver-title {
              margin-bottom:
                1mm !important;

              font-size: 7px !important;

              font-weight:
                700 !important;
            }


            .p-receiver-grid {
              display: grid !important;

              grid-template-columns:
                1fr 1fr !important;

              gap: 30px !important;
            }


            .p-receiver-field {
              display: flex !important;

              align-items:
                center !important;

              width: 70% !important;

              min-height:
                4.5mm !important;

              margin-top:
                0.5mm !important;

              padding:
                0.8mm 1.2mm !important;

              border:
                1px solid #C9CEDA !important;

              border-radius:
                2px !important;

              font-size: 6.5px !important;
            }


            .p-receiver-sign {
              margin-top:
                1.5mm !important;

              font-size:
                6px !important;
            }


            /* ===============================================
               PRINT SAFETY
            =============================================== */

            .p-table tr,
            .p-table td,
            .p-table th {
              page-break-inside:
                avoid !important;

              break-inside:
                avoid !important;
            }


            .print-area img {
              visibility:
                visible !important;
            }


            .print-area * {
              -webkit-print-color-adjust:
                exact !important;

              print-color-adjust:
                exact !important;
            }

          }

        `}
      </style>
    </div>
  );
}

/* =========================================================
   SCREEN STYLES
========================================================= */

const cardStyle = {
  background: "#fff",
  padding: 25,
  borderRadius: 12,
  boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  marginBottom: 30,
  border: `1px solid ${colors.border}`,
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 20,
};

const mainTitle = {
  textAlign: "center",
  marginBottom: 30,
  color: colors.heading,
  letterSpacing: "0.5px",
};

const projectRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 15,
};

const toRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: 10,
};

const inputBox = {
  width: "100%",
  padding: 12,
  borderRadius: 8,
  border: `1px solid ${colors.border}`,
  outline: "none",
  boxSizing: "border-box",
};

const infoBox = {
  padding: 12,
  borderRadius: 10,
  background: "#F0F1F6",
  border: `1px solid ${colors.border}`,
};

const sectionHeading = {
  fontWeight: "bold",
  marginBottom: 8,
  color: colors.heading,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 20,
};

const thStyle = {
  background: "#E4E8FF",
  padding: "12px 10px",
  border: `1px solid ${colors.border}`,
  fontSize: 16,
  fontWeight: 700,
  color: "#1A1C2B",
};

const tdStyle = {
  padding: 8,
  border: `1px solid ${colors.border}`,
  textAlign: "center",
};

const smallInput = {
  width: "100%",
  padding: 3,
  border: "none",
  outline: "none",
  textAlign: "center",
  boxSizing: "border-box",
};

const fullInput = {
  width: "100%",
  padding: 3,
  border: "none",
  outline: "none",
  boxSizing: "border-box",
};

const selectBox = {
  width: "100%",
  padding: 6,
  borderRadius: 6,
  border: "1px solid #ccc",
  boxSizing: "border-box",
};

const deleteBtn = {
  padding: "5px 10px",
  background: "#cc0000",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
};

const addBtn = {
  marginTop: 12,
  padding: "8px 15px",
  background: colors.brand,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};

const footerBar = {
  position: "sticky",
  bottom: 0,
  background: "#fff",
  padding: 15,
  borderTop: `1px solid ${colors.border}`,
  textAlign: "center",
  marginTop: 20,
};

const footerBtn = {
  padding: "10px 25px",
  fontSize: 15,
  background: colors.brand,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  margin: "0 8px",
  cursor: "pointer",
};

const checkLabel = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 18,
  fontWeight: 600,
};

const reasonGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(3, auto)",
  gap: "35px 45px",
  marginTop: 15,
  alignItems: "center",
};

const reasonLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  fontSize: 18,
  fontWeight: 600,
};

const checkboxStyle = {
  transform: "scale(1.6)",
  accentColor: colors.brand,
};

const reasonSubText = {
  marginLeft: 8,
  fontSize: 15,
  fontWeight: 400,
};

const sendingLabel = {
  display: "inline-flex",
  alignItems: "center",
  marginRight: 35,
  gap: 10,
  fontSize: 18,
  fontWeight: 600,
};

const preparedGrid = {
  marginTop: 25,
  display: "grid",
  gridTemplateColumns: "400px 1fr",
  gap: 40,
  alignItems: "start",
};
